import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { ListsRepository } from '../domain/lists.repository';
import {
  ActivityListAssociation,
  ActivityOption,
  AddListItemInput,
  LifeList,
  ListInput,
  ListItem,
  ListWithCount,
  TaskDefinitionOption,
  TaskListAssociation
} from '../models/list.model';

interface ListRow {
  id: number;
  name: string;
  description: string | null;
  kind: LifeList['kind'];
  is_archived: boolean;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

@Injectable()
export class SupabaseListsRepository extends ListsRepository {
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async getLists(): Promise<ListWithCount[]> {
    const { data: rows, error } = await this.client
      .from('lists')
      .select('id,name,description,kind,is_archived,archived_at,created_at,updated_at')
      .order('created_at', { ascending: true });
    if (error) throw error;

    const listRows = (rows ?? []) as ListRow[];
    if (!listRows.length) return [];

    const listIds = listRows.map(row => row.id);
    const [{ data: itemRows, error: itemError }, { data: linkRows, error: linkError }] = await Promise.all([
      this.client
        .from('list_items')
        .select('list_id,is_completed,archived_at')
        .in('list_id', listIds),
      this.client
        .from('task_lists')
        .select('list_id')
        .in('list_id', listIds)
    ]);
    if (itemError) throw itemError;
    if (linkError) throw linkError;

    return listRows.map(row => {
      const listItems = (itemRows ?? []).filter(item => item.list_id === row.id);
      const visibleItems = listItems.filter(item => item.archived_at === null);
      return {
        ...this.mapList(row),
        activeItemCount: visibleItems.filter(item => !item.is_completed).length,
        completedItemCount: visibleItems.filter(item => item.is_completed).length,
        historicalItemCount: listItems.length,
        linkedTaskCount: (linkRows ?? []).filter(link => link.list_id === row.id).length
      };
    });
  }

  async getList(id: number): Promise<ListWithCount> {
    const { data: row, error } = await this.client
      .from('lists')
      .select('id,name,description,kind,is_archived,archived_at,created_at,updated_at')
      .eq('id', id)
      .single();
    if (error) throw error;

    const [{ data: itemRows, error: itemError }, { count, error: linkError }] = await Promise.all([
      this.client
        .from('list_items')
        .select('is_completed,archived_at')
        .eq('list_id', id),
      this.client
        .from('task_lists')
        .select('*', { count: 'exact', head: true })
        .eq('list_id', id)
    ]);
    if (itemError) throw itemError;
    if (linkError) throw linkError;

    const visibleItems = (itemRows ?? []).filter(item => item.archived_at === null);
    return {
      ...this.mapList(row as ListRow),
      activeItemCount: visibleItems.filter(item => !item.is_completed).length,
      completedItemCount: visibleItems.filter(item => item.is_completed).length,
      historicalItemCount: (itemRows ?? []).length,
      linkedTaskCount: count ?? 0
    };
  }

  async createList(input: ListInput): Promise<LifeList> {
    const { data, error } = await this.client
      .from('lists')
      .insert({
        name: input.name,
        description: input.description,
        kind: input.kind,
        is_archived: false,
        archived_at: null
      })
      .select('id,name,description,kind,is_archived,archived_at,created_at,updated_at')
      .single();
    if (error) throw error;
    return this.mapList(data as ListRow);
  }

  async updateList(id: number, input: ListInput): Promise<LifeList> {
    const { data, error } = await this.client
      .from('lists')
      .update({ name: input.name, description: input.description, kind: input.kind })
      .eq('id', id)
      .select('id,name,description,kind,is_archived,archived_at,created_at,updated_at')
      .single();
    if (error) throw error;
    return this.mapList(data as ListRow);
  }

  async setListArchived(id: number, archived: boolean): Promise<void> {
    const { error } = await this.client
      .from('lists')
      .update({
        is_archived: archived,
        archived_at: archived ? new Date().toISOString() : null
      })
      .eq('id', id);
    if (error) throw error;
  }

  async deleteList(id: number): Promise<void> {
    const { error } = await this.client.from('lists').delete().eq('id', id);
    if (error) throw error;
  }

  async getItems(listId: number): Promise<ListItem[]> {
    const { data, error } = await this.client
      .from('list_items')
      .select('id,list_id,parent_item_id,title,description,position,is_completed,completed_at,archived_at')
      .eq('list_id', listId)
      .is('archived_at', null)
      .order('position', { ascending: true });
    if (error) throw error;

    return (data ?? []).map(row => ({
      id: row.id,
      listId: row.list_id,
      parentItemId: row.parent_item_id,
      title: row.title,
      description: row.description,
      position: row.position,
      isCompleted: row.is_completed,
      completedAt: row.completed_at,
      archivedAt: row.archived_at
    }));
  }

  async addItem(listId: number, input: AddListItemInput): Promise<ListItem> {
    let positionQuery = this.client
      .from('list_items')
      .select('position')
      .eq('list_id', listId);

    positionQuery = input.parentItemId == null
      ? positionQuery.is('parent_item_id', null)
      : positionQuery.eq('parent_item_id', input.parentItemId);

    const { data: positions, error: positionError } = await positionQuery
      .order('position', { ascending: false })
      .limit(1);
    if (positionError) throw positionError;

    const nextPosition = positions?.length ? positions[0].position + 1 : 0;
    const { data, error } = await this.client
      .from('list_items')
      .insert({
        list_id: listId,
        parent_item_id: input.parentItemId ?? null,
        title: input.title,
        position: nextPosition,
        is_completed: false,
        completed_at: null,
        archived_at: null
      })
      .select('id,list_id,parent_item_id,title,description,position,is_completed,completed_at,archived_at')
      .single();
    if (error) throw error;

    return {
      id: data.id,
      listId: data.list_id,
      parentItemId: data.parent_item_id,
      title: data.title,
      description: data.description,
      position: data.position,
      isCompleted: data.is_completed,
      completedAt: data.completed_at,
      archivedAt: data.archived_at
    };
  }

  async renameItem(itemId: number, title: string): Promise<void> {
    const { error } = await this.client.from('list_items').update({ title }).eq('id', itemId);
    if (error) throw error;
  }

  async setItemCompleted(itemId: number, completed: boolean): Promise<void> {
    const { error } = await this.client
      .from('list_items')
      .update({
        is_completed: completed,
        completed_at: completed ? new Date().toISOString() : null
      })
      .eq('id', itemId);
    if (error) throw error;
  }

  async archiveItem(itemId: number): Promise<void> {
    const archivedAt = new Date().toISOString();
    const { error: childrenError } = await this.client
      .from('list_items')
      .update({ archived_at: archivedAt })
      .eq('parent_item_id', itemId)
      .is('archived_at', null);
    if (childrenError) throw childrenError;

    const { error } = await this.client
      .from('list_items')
      .update({ archived_at: archivedAt })
      .eq('id', itemId)
      .is('archived_at', null);
    if (error) throw error;
  }

  async moveItem(itemId: number, direction: -1 | 1): Promise<void> {
    const { error } = await this.client.rpc('move_list_item', {
      p_item_id: itemId,
      p_direction: direction
    });
    if (error) throw error;
  }

  async purgeCompletedItems(listId: number): Promise<number> {
    const { data, error } = await this.client.rpc('archive_completed_list_items', {
      p_list_id: listId
    });
    if (error) throw error;
    return typeof data === 'number' ? data : Number(data ?? 0);
  }

  async getTaskDefinitions(listId: number): Promise<TaskDefinitionOption[]> {
    const [{ data: definitions, error: definitionsError }, { data: links, error: linksError }] = await Promise.all([
      this.client
        .from('task_definitions')
        .select('id,title,kind,is_active,archived_at')
        .order('title', { ascending: true }),
      this.client
        .from('task_lists')
        .select('task_definition_id')
        .eq('list_id', listId)
    ]);
    if (definitionsError) throw definitionsError;
    if (linksError) throw linksError;

    const linkedIds = new Set((links ?? []).map(link => link.task_definition_id));
    return (definitions ?? [])
      .filter(definition => definition.archived_at === null || linkedIds.has(definition.id))
      .map(definition => ({
        id: definition.id,
        title: definition.title,
        kind: definition.kind,
        isActive: definition.is_active,
        archivedAt: definition.archived_at,
        linked: linkedIds.has(definition.id)
      }));
  }

  async getTaskListAssociations(): Promise<TaskListAssociation[]> {
    const { data: links, error: linksError } = await this.client
      .from('task_lists')
      .select('task_definition_id,list_id');
    if (linksError) throw linksError;
    if (!links?.length) return [];

    const taskDefinitionIds = [...new Set(links.map(link => link.task_definition_id))];
    const listIds = [...new Set(links.map(link => link.list_id))];

    const [definitionsResult, listsResult] = await Promise.all([
      this.client
        .from('task_definitions')
        .select('id,title,kind')
        .in('id', taskDefinitionIds),
      this.client
        .from('lists')
        .select('id,name,description,kind,is_archived,archived_at,created_at,updated_at')
        .in('id', listIds)
    ]);
    if (definitionsResult.error) throw definitionsResult.error;
    if (listsResult.error) throw listsResult.error;

    const definitionsById = new Map(
      (definitionsResult.data ?? []).map(definition => [definition.id, definition])
    );
    const listsById = new Map(
      ((listsResult.data ?? []) as ListRow[]).map(row => [row.id, this.mapList(row)])
    );

    return links
      .map(link => {
        const definition = definitionsById.get(link.task_definition_id);
        const list = listsById.get(link.list_id);
        if (!definition || !list) return null;

        return {
          taskDefinitionId: definition.id,
          taskTitle: definition.title,
          taskKind: definition.kind,
          list
        } satisfies TaskListAssociation;
      })
      .filter((association): association is TaskListAssociation => association !== null)
      .sort((a, b) => a.taskTitle.localeCompare(b.taskTitle) || a.list.name.localeCompare(b.list.name));
  }

  async setTaskLinked(listId: number, taskDefinitionId: number, linked: boolean): Promise<void> {
    if (linked) {
      const { error } = await this.client
        .from('task_lists')
        .upsert({ list_id: listId, task_definition_id: taskDefinitionId }, {
          onConflict: 'task_definition_id,list_id',
          ignoreDuplicates: true
        });
      if (error) throw error;
      return;
    }

    const { error } = await this.client
      .from('task_lists')
      .delete()
      .eq('list_id', listId)
      .eq('task_definition_id', taskDefinitionId);
    if (error) throw error;
  }

  async getActivities(listId: number): Promise<ActivityOption[]> {
    const [{ data: activities, error: activitiesError }, { data: links, error: linksError }] = await Promise.all([
      this.client
        .from('time_activities')
        .select('id,name,archived_at')
        .order('name', { ascending: true }),
      this.client
        .from('activity_lists')
        .select('activity_id')
        .eq('list_id', listId)
    ]);
    if (activitiesError) throw activitiesError;
    if (linksError) throw linksError;

    const linkedIds = new Set((links ?? []).map(link => link.activity_id));
    return (activities ?? [])
      .filter(activity => activity.archived_at === null || linkedIds.has(activity.id))
      .map(activity => ({
        id: activity.id,
        name: activity.name,
        archivedAt: activity.archived_at,
        linked: linkedIds.has(activity.id)
      }));
  }

  async getActivityListAssociations(): Promise<ActivityListAssociation[]> {
    const { data: links, error: linksError } = await this.client
      .from('activity_lists')
      .select('activity_id,list_id');
    if (linksError) throw linksError;
    if (!links?.length) return [];

    const activityIds = [...new Set(links.map(link => link.activity_id))];
    const listIds = [...new Set(links.map(link => link.list_id))];

    const [activitiesResult, listsResult] = await Promise.all([
      this.client
        .from('time_activities')
        .select('id,name')
        .in('id', activityIds),
      this.client
        .from('lists')
        .select('id,name,description,kind,is_archived,archived_at,created_at,updated_at')
        .in('id', listIds)
    ]);
    if (activitiesResult.error) throw activitiesResult.error;
    if (listsResult.error) throw listsResult.error;

    const activitiesById = new Map(
      (activitiesResult.data ?? []).map(activity => [activity.id, activity])
    );
    const listsById = new Map(
      ((listsResult.data ?? []) as ListRow[]).map(row => [row.id, this.mapList(row)])
    );

    return links
      .map(link => {
        const activity = activitiesById.get(link.activity_id);
        const list = listsById.get(link.list_id);
        if (!activity || !list) return null;
        return {
          activityId: activity.id,
          activityName: activity.name,
          list
        } satisfies ActivityListAssociation;
      })
      .filter((association): association is ActivityListAssociation => association !== null)
      .sort((a, b) => a.activityName.localeCompare(b.activityName) || a.list.name.localeCompare(b.list.name));
  }

  async setActivityLinked(listId: number, activityId: number, linked: boolean): Promise<void> {
    if (linked) {
      const { error } = await this.client
        .from('activity_lists')
        .upsert({ list_id: listId, activity_id: activityId }, {
          onConflict: 'activity_id,list_id',
          ignoreDuplicates: true
        });
      if (error) throw error;
      return;
    }

    const { error } = await this.client
      .from('activity_lists')
      .delete()
      .eq('list_id', listId)
      .eq('activity_id', activityId);
    if (error) throw error;
  }

  private mapList(row: ListRow): LifeList {
    return {
      id: row.id,
      name: row.name,
      description: row.description,
      kind: row.kind,
      isArchived: row.is_archived,
      archivedAt: row.archived_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }
}
